import React, { useCallback, useMemo, useState } from 'react';
import type { TargetType } from '@atlassiansox/cross-flow-base-types';
import { useCrossFlowEditionParity } from '@jira/cross-flow-edition-parity/src/services/index.tsx';
import Heading from '@atlaskit/heading';
import { Text } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import { TEMPLATE_KEY as PRIORITIZATION_KEY } from '@atlassian/jira-project-template-select-v2/src/utils/templates/product-discovery/prioritization/index.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useFlagsService } from '@atlassian/jira-flags';
import projectCreateMessages from '@atlassian/jira-project-create-form/src/ui/messages.tsx';
import type { CreateProjectResponse } from '@atlassian/jira-project-create-common/src/services/create-project/types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { OnboardingModalComponent } from '../../common/ui/modal/index.tsx';
import { OnboardingGrid } from '../../common/ui/onboarding-grid/index.tsx';
import { OnboardingContent } from '../../common/ui/content/index.tsx';
import { LegalNotice } from '../../common/ui/content/legal-notice/index.tsx';
import backgroundLight from '../../common/assets/background-light.png';
import backgroundDark from '../../common/assets/background-dark.png';
import { Features } from '../../common/ui/features/index.tsx';
import { TabContent } from '../../common/ui/tab-content/index.tsx';
import { getTemplateContentFromKey } from '../../utils/get-template-content-from-key/index.tsx';
import { useSubmitProjectCreate } from '../../common/services/submit-project-create/index.tsx';
import { getTemplateTmpKeyFromKey } from '../../utils/get-template-tmp-key-from-key/index.tsx';
import type { TemplateKey } from '../../utils/types.tsx';

import { OnboardingModalContextProvider } from '../../controllers/onboarding-modal-context/index.tsx';
import messages from './messages.tsx';

type ProjectDirectoryOnboardingModalProps = {
	targetProduct: TargetType;
	sourceComponent: string;
	templateKey?: TemplateKey;
};

export const ProjectDirectoryOnboardingModal = ({
	targetProduct,
	templateKey = PRIORITIZATION_KEY,
	sourceComponent,
}: ProjectDirectoryOnboardingModalProps) => {
	const { push } = useRouterActions();
	const { showFlag } = useFlagsService();
	const { formatMessage, locale } = useIntl();
	const [selectedFeature, setSelectedFeature] = useState(0);
	const [open, setOpen] = useState(true);

	const { upgradeCCPEntitlement, entitlementUpgrading, loadingUpgradeData } =
		useCrossFlowEditionParity({
			sourceProduct: 'jira-software.ondemand',
			targetProduct,
		});

	const handleFeatureUpdate = useCallback(
		(index: number) => setSelectedFeature(index),
		[setSelectedFeature],
	);

	const templateContent = useMemo(() => {
		return getTemplateContentFromKey(templateKey);
	}, [templateKey]);

	const onSuccess = useCallback(
		(createProjectResponse: CreateProjectResponse) => {
			push(createProjectResponse.returnUrl);
		},
		[push],
	);

	const onFail = useCallback(
		(_error: Error) => {
			showFlag({
				type: 'error',
				id: 'project-creation-failure',
				title: formatMessage(
					expVal('project-terminology-refresh', 'isEnabled', false)
						? projectCreateMessages.createProjectFailureTitleNonFinalGalaxia
						: projectCreateMessages.createProjectFailureTitleNonFinal,
				),
				description: formatMessage(projectCreateMessages.createProjectFailureDescriptionNonFinal),
				messageId:
					'cross-flow-onboarding-modal.ui.project-directory-onboarding-modal.show-flag.error',
				messageType: 'transactional',
			});
		},
		[formatMessage, showFlag],
	);

	const [submitProjectCreate, isProjectCreateLoading, _hasProjectCreateError] =
		useSubmitProjectCreate(onSuccess, onFail);

	const onClick = useCallback(async () => {
		if (!entitlementUpgrading && !loadingUpgradeData) {
			upgradeCCPEntitlement({
				tosPrivacyText: 'Terms of Service and Privacy Policy accepted',
				locale,
			});
		}

		await submitProjectCreate({
			payload: {
				templateKey: getTemplateTmpKeyFromKey(templateKey),
				name: formatMessage(templateContent.templateTitle),
			},
			analyticsAttributes: {},
		});
	}, [
		entitlementUpgrading,
		formatMessage,
		loadingUpgradeData,
		locale,
		submitProjectCreate,
		templateContent.templateTitle,
		templateKey,
		upgradeCCPEntitlement,
	]);

	return (
		<ContextualAnalyticsData
			attributes={{
				featureItemIndex: selectedFeature,
				templateKey,
				message: templateContent.features[selectedFeature].description.defaultMessage,
			}}
		>
			<OnboardingModalContextProvider targetProduct={targetProduct} open={open}>
				<OnboardingModalComponent
					targetProduct={targetProduct}
					sourceComponent={sourceComponent}
					ariaLabel={formatMessage(messages.ariaLabel)}
				>
					<OnboardingGrid enableCrossButton onClose={() => setOpen(false)}>
						<OnboardingGrid.ContentSection>
							<OnboardingContent>
								<Heading size="large">{formatMessage(messages.modalHeading)}</Heading>
								<Features>
									{templateContent.features.map((item, i) => (
										<Features.Item
											isActive={selectedFeature === i}
											key={`cross-flow-onboarding-modal-${i}-item`}
											onClick={() => handleFeatureUpdate(i)}
											index={i}
										>
											<item.icon label="" color="var(--ds-icon-selected)" key={i} />
											<Text color="inherit">{formatMessage(item.description)}</Text>
										</Features.Item>
									))}
								</Features>
								<LegalNotice />
							</OnboardingContent>

							<OnboardingGrid.Controls>
								<OnboardingGrid.ControlItem variant="cancel" onClick={() => setOpen(false)} />
								<OnboardingGrid.ControlItem
									variant="get-started"
									appearance="primary"
									isLoading={isProjectCreateLoading || entitlementUpgrading}
									onClick={onClick}
									disabled={loadingUpgradeData}
								/>
							</OnboardingGrid.Controls>
						</OnboardingGrid.ContentSection>
						<OnboardingGrid.ImageSection
							backgroundLight={backgroundLight}
							backgroundDark={backgroundDark}
						>
							<TabContent
								onChange={handleFeatureUpdate}
								selected={selectedFeature}
								content={templateContent}
								templateKey={templateKey}
							/>
						</OnboardingGrid.ImageSection>
					</OnboardingGrid>
				</OnboardingModalComponent>
			</OnboardingModalContextProvider>
		</ContextualAnalyticsData>
	);
};
